/**
 * Pure helper: turns the statement builder's current state into the snapshot
 * payload that will eventually be saved to the backend.
 *
 * Data in, data out — no React state, no side effects, no backend calls.
 * The returned shape mirrors the `statement` + `statement_line` Prisma models.
 *
 * Intentionally omitted (generated/added at save-time, not here):
 *   - reference        (backend generates on save)
 *   - name             (not captured in builder yet)
 *   - created_by_*     (added from the authenticated user at save-time)
 *
 * Reward numbers come from the live API (parentRewardsCode / farmRewardsCode).
 * Reward ids have no API source yet and are emitted as null.
 */

const lastDayOfMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

/**
 * Resolve period_mode + period_start + period_end from the date controls.
 * - month mode:  "2026-06" -> 2026-06-01 .. 2026-06-30
 * - custom mode: customStart .. customEnd (already YYYY-MM-DD)
 */
export const resolvePeriod = ({ dateMode, selectedMonth, customStart, customEnd }) => {
  if (dateMode === "custom") {
    return {
      period_mode: "CUSTOM",
      period_start: customStart || null,
      period_end: customEnd || null
    };
  }

  const [yearStr, monthStr] = (selectedMonth || "").split("-");
  const year = Number(yearStr);
  const monthIndex = Number(monthStr) - 1;

  if (!year || Number.isNaN(monthIndex)) {
    return { period_mode: "MONTH", period_start: null, period_end: null };
  }

  const pad = (n) => String(n).padStart(2, "0");
  const start = `${year}-${pad(monthIndex + 1)}-01`;
  const end = `${year}-${pad(monthIndex + 1)}-${pad(lastDayOfMonth(year, monthIndex))}`;
  return { period_mode: "MONTH", period_start: start, period_end: end };
};

/**
 * Derive scope_type + scope_label + primary parent/child info from the
 * selected invoice lines.
 *
 * Rules (per product spec):
 *   1 parent + 1 child       -> SINGLE_CHILD   (label: child/farm name)
 *   1 parent + many children -> SINGLE_PARENT  (label: "N farms")
 *   many parents             -> CONSOLIDATED   (label: "N parent accounts")
 */
const resolveScope = (lines) => {
  const parentNames = [...new Set(lines.map((l) => l.parent_account_name).filter(Boolean))];
  const childNames = [...new Set(lines.map((l) => l.child_account_name).filter(Boolean))];

  if (parentNames.length > 1) {
    return {
      scope_type: "CONSOLIDATED",
      scope_label: `${parentNames.length} parent accounts`,
      primary_parent_account_id: null,
      primary_parent_account_name: null,
      primary_child_account_id: null,
      primary_child_account_name: null
    };
  }

  // Exactly one parent at this point (lines is non-empty when called).
  const onlyParent = lines.find((l) => l.parent_account_name)?.parent_account_name || null;
  const primaryParentId = lines.find((l) => l.parent_account_id)?.parent_account_id || null;

  if (childNames.length <= 1) {
    const onlyChild = lines.find((l) => l.child_account_name) || {};
    return {
      scope_type: "SINGLE_CHILD",
      scope_label: onlyChild.child_account_name || onlyParent || "",
      primary_parent_account_id: primaryParentId,
      primary_parent_account_name: onlyParent,
      primary_child_account_id: onlyChild.child_account_id || null,
      primary_child_account_name: onlyChild.child_account_name || null
    };
  }

  return {
    scope_type: "SINGLE_PARENT",
    scope_label: `${childNames.length} farms`,
    primary_parent_account_id: primaryParentId,
    primary_parent_account_name: onlyParent,
    primary_child_account_id: null,
    primary_child_account_name: null
  };
};

/**
 * Map a single builder invoice row to a statement_line snapshot.
 */
const toLine = (inv) => ({
  source_invoice_id: inv.id ?? null,
  invoice_number: inv.invoiceNumber ?? null,
  invoice_date: inv.invoiceDate ?? null,
  dealer_name: inv.dealerName ?? null,
  invoice_total: inv.invoiceTotal ?? null,
  rebate_amount: inv.rebateAmount ?? null,
  parent_account_id: inv.parentAccountId ?? null,
  parent_account_name: inv.parentAccount ?? null,
  parent_reward_id: inv.parentRewardId ?? null,
  parent_reward_number: inv.parentRewardsCode ?? null,
  child_account_id: inv.farmId ?? null,
  child_account_name: inv.farmName ?? null,
  child_reward_id: inv.childRewardId ?? null,
  child_reward_number: inv.farmRewardsCode ?? null
});

/**
 * Build the full statement snapshot payload from current builder state.
 *
 * @param {Object} args
 * @param {"month"|"custom"} args.dateMode
 * @param {string} args.selectedMonth   e.g. "2026-06"
 * @param {string} args.customStart     e.g. "2026-06-01"
 * @param {string} args.customEnd       e.g. "2026-06-30"
 * @param {Object} args.columns         visibility map, e.g. { invoiceNumber: true, ... }
 * @param {Array}  args.visibleInvoices already-filtered invoice rows shown in the preview
 * @param {number} args.grandTotal
 * @param {number} args.grandRebate
 * @returns {Object} snapshot payload (header fields + lines[])
 */
export const buildStatementSnapshot = ({
  dateMode,
  selectedMonth,
  customStart,
  customEnd,
  columns,
  visibleInvoices,
  grandTotal,
  grandRebate
}) => {
  const lines = (visibleInvoices || []).map(toLine);
  const period = resolvePeriod({ dateMode, selectedMonth, customStart, customEnd });
  const scope = resolveScope(lines);

  return {
    ...period,
    column_config: { ...columns },
    ...scope,
    invoice_count: lines.length,
    grand_total: grandTotal ?? null,
    grand_rebate: grandRebate ?? null,
    lines
  };
};

export default buildStatementSnapshot;
